import { BUY_ME_A_COFFEE_URL } from '../../config/supportLinks.ts'
import { bootstrapRealtime, getDisplayNickname } from '../../app/zionApp.ts'
import { formatBuildLabel, ZION_BUILD } from '../../buildInfo.ts'
import {
  downloadBackupBlob,
  exportEncryptedBackup,
  importEncryptedBackup,
  BACKUP_FILE_ACCEPT,
} from '../../backup/ZionBackup.ts'
import { lockSession, markSessionUnlocked, changeMasterPasswordFromSettings } from '../../identity/ZionIdentityManager.ts'
import { applyAppLayout } from '../layout.ts'
import { isAppleDevice } from '../layout.ts'
import { isCapacitorNative } from '../../api/ZionServer.ts'
import { renderQrDataUrl } from '../../pairing/qrCode.ts'
import {
  beginTotpEnrollment,
  cancelTotpEnrollment,
  confirmTotpEnrollment,
  disableTotp,
  isTotpEnabled,
} from '../../storage/totpStore.ts'
import {
  getSoundVolume,
  isSoundMuted,
  setSoundMuted,
  setSoundVolume,
} from '../../storage/soundSettingsStore.ts'
import { playAudibleFeedbackTone } from '../../notifications/audioUnlock.ts'
import { factoryResetAllData } from '../../app/factoryReset.ts'
import { exitApp } from '../../app/exitApp.ts'
import {
  getAllowExternalLinks,
  getAutoMediaDownload,
  getRequireUnlockOnOpen,
  getWipeOnExit,
  setAllowExternalLinks,
  setAutoMediaDownload,
  setRequireUnlockOnOpen,
  setWipeOnExit,
} from '../../storage/privacySettingsStore.ts'
import { getStoredLocale, saveLocale, SUPPORTED_LOCALES } from '../../storage/languageStore.ts'
import { setActiveLocale, t, tf } from '../../i18n/index.ts'
import type { LocaleCode } from '../../i18n/types.ts'
import { getSessionToken } from '../../storage/sessionStore.ts'
import { syncMediaPrefs } from '../../socket2/ZionSocketClient.ts'
import { unlockNotificationAudio, ensureDesktopNotificationPermission } from '../../notifications/messageNotify.ts'
import {
  beginNotificationPermissionRequest,
  disableWebPush,
  enableWebPush,
  iosNeedsHomeInstall,
  notificationPermissionDeniedHint,
  pushModeLabel,
  pushPermission,
  pushStatusLabelAsync,
  resetAndReenableWebPush,
} from '../../services/pushService.ts'
import { isNativePushPlatform } from '../../push/nativePush.ts'
import { listLinkedDevices, pushHistoryToDevice } from '../../deviceLink/deviceLinkService.ts'
import { shouldShowDeviceLinkQrOnAuth } from '../../deviceLink/deviceLinkReceiverPanel.ts'
import { detectPlatform } from '../../deviceLink/deviceLinkStore.ts'
import { disconnectSocket } from '../../socket2/ZionSocketClient.ts'
import { appendBlocks, escapeHtml } from '../dom.ts'
import { navigate, isStandalonePwa } from '../router.ts'
import { wireSwipeBack } from '../swipeBack.ts'
import { applyTheme, getCurrentTheme, type ZionThemeId } from '../themes.ts'
import { FONTS_CONFIG, getUserChatFontId, setUserChatFont, type ChatFontId } from '../fontsConfig.ts'

function setStatus(el: HTMLElement | null, text: string, kind: 'ok' | 'err' | 'muted' = 'muted'): void {
  if (!el) return
  el.textContent = text
  el.className = `status ${kind}`
}

let settingsSwipeCleanup: (() => void) | null = null

export function clearSettingsScreenHooks(): void {
  if (settingsSwipeCleanup) {
    settingsSwipeCleanup()
    settingsSwipeCleanup = null
  }
}

function toggleRow(id: string, label: string, checked: boolean, hint?: string): string {
  const hintHtml = hint ? `<p class="status muted small toggle-hint">${escapeHtml(hint)}</p>` : ''
  return `
    <div class="toggle-row">
      <label class="toggle-label" for="${id}">${escapeHtml(label)}</label>
      <label class="zion-switch">
        <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} />
        <span class="zion-switch-slider"></span>
      </label>
    </div>
    ${hintHtml}
  `
}

function showWarnModal(title: string, body: string, onConfirm: () => void): void {
  const overlay = document.createElement('div')
  overlay.className = 'modal-overlay warn-modal'
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.innerHTML = `
    <div class="modal-card warn">
      <h3 class="warn-title">${escapeHtml(title)}</h3>
      <p class="warn-body">${escapeHtml(body)}</p>
      <div class="modal-actions">
        <button type="button" class="secondary warn-cancel" id="modal-cancel">${escapeHtml(t('btn_back'))}</button>
        <button type="button" class="danger-btn warn-confirm" id="modal-confirm">OK</button>
      </div>
    </div>
  `
  const close = () => {
    overlay.remove()
    document.body.style.overflow = ''
  }
  document.body.style.overflow = 'hidden'
  document.body.appendChild(overlay)
  overlay.querySelector('#modal-cancel')!.addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  overlay.querySelector('#modal-confirm')!.addEventListener('click', () => {
    close()
    onConfirm()
  })
}

function refreshPushSettingsUi(root: HTMLElement): void {
  const statusLine = root.querySelector('#push-status-line') as HTMLElement | null
  const deniedHint = root.querySelector('#push-denied-hint') as HTMLElement | null
  const btnEnable = root.querySelector('#btn-enable-push') as HTMLButtonElement | null
  const btnReset = root.querySelector('#btn-reset-push') as HTMLButtonElement | null
  const btnDesktop = root.querySelector('#btn-desktop-notify') as HTMLButtonElement | null
  if (!statusLine) return

  void pushStatusLabelAsync().then((label) => {
    statusLine.textContent = `${t('settings_push_mode_prefix')} ${pushModeLabel()} — ${label}`
  })

  const perm = pushPermission()
  const denied = perm === 'denied'
  if (deniedHint) {
    deniedHint.hidden = !denied
    deniedHint.textContent = denied ? notificationPermissionDeniedHint() : ''
  }
  if (btnEnable) btnEnable.disabled = denied || iosNeedsHomeInstall()
  if (btnReset) btnReset.disabled = denied
  if (btnDesktop) btnDesktop.disabled = denied
}

export async function renderSettingsScreen(root: HTMLElement): Promise<void> {
  clearSettingsScreenHooks()
  applyAppLayout('page')
  root.classList.add('screen-settings')
  const nick = await getDisplayNickname()
  const currentTheme = getCurrentTheme()
  const totpOn = isTotpEnabled()
  const soundVol = getSoundVolume()
  const soundMuted = isSoundMuted()
  const showWebPushSettings = !isCapacitorNative() && !isNativePushPlatform()

  const currentLocale = getStoredLocale() ?? 'it'
  const langOptions = SUPPORTED_LOCALES.map(
    (l) => `<option value="${l.code}"${l.code === currentLocale ? ' selected' : ''}>${l.label}</option>`,
  ).join('')

  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="topbar premium-topbar">
        <button type="button" class="icon-btn" id="btn-back">←</button>
        <h2 class="topbar-title">${escapeHtml(t('settings_title'))}</h2>
      </header>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_language'))}</h2>
        <label for="settings-lang">${escapeHtml(t('settings_language_label'))}</label>
        <select id="settings-lang" class="lang-select">${langOptions}</select>
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_identity'))}</h2>
        <p class="status ok">@${escapeHtml(nick)}</p>
        <p class="status muted small">${escapeHtml(t('settings_identity_hint'))}</p>
        <button type="button" class="secondary" id="btn-add-home">${escapeHtml(t('settings_btn_add_home'))}</button>
        <button type="button" class="secondary" id="btn-lock">${escapeHtml(t('btn_lock'))}</button>
      </section>

      <section class="card" id="devices-card">
        <h2 class="card-title">${escapeHtml(t('settings_devices_title'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_devices_hint'))}</p>
        <ul id="linked-devices-list" class="linked-devices-list"></ul>
        <button type="button" id="btn-link-new-device">${escapeHtml(t('settings_btn_link_device'))}</button>
        <button type="button" class="secondary" id="btn-sync-pc-history" hidden>${escapeHtml(t('settings_btn_sync_pc'))}</button>
        <p id="device-sync-status" class="status muted cyber-status"></p>
      </section>

      <section class="card highlight">
        <h2 class="card-title">${escapeHtml(t('section_updates'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_version_prefix'))} <strong id="app-version">${escapeHtml(formatBuildLabel())}</strong> (${escapeHtml(ZION_BUILD)})</p>
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_theme'))}</h2>
        <div class="theme-switch" id="theme-switch" role="group" aria-label="${escapeHtml(t('section_theme'))}">
          <button type="button" class="theme-switch-btn${currentTheme === 'default' ? ' is-active' : ''}" data-theme="default">Void &amp; Cobalt</button>
          <button type="button" class="theme-switch-btn${currentTheme === 'matrix' ? ' is-active' : ''}" data-theme="matrix">Matrix</button>
          <button type="button" class="theme-switch-btn${currentTheme === 'white_platinum' ? ' is-active' : ''}" data-theme="white_platinum">White Platinum</button>
        </div>
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('settings_font_title'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_font_hint'))}</p>
        <div class="font-grid" id="font-grid"></div>
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_privacy_network'))}</h2>
        ${toggleRow('toggle-external-links', t('toggle_external_links'), getAllowExternalLinks(), t('hint_external_links'))}
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_media'))}</h2>
        ${toggleRow('toggle-auto-media', t('toggle_auto_media'), getAutoMediaDownload(), t('hint_auto_media'))}
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_security'))}</h2>
        ${toggleRow('toggle-require-unlock', t('toggle_require_unlock'), getRequireUnlockOnOpen(), t('hint_require_unlock'))}
      </section>

      <section class="card" id="master-password-card">
        <h2 class="card-title">${escapeHtml(t('section_change_password'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_change_password_hint'))}</p>
        <label for="pwd-current">${escapeHtml(t('settings_pwd_current'))}</label>
        <input id="pwd-current" type="password" autocomplete="current-password" />
        <label for="pwd-new">${escapeHtml(t('settings_pwd_new'))}</label>
        <input id="pwd-new" type="password" autocomplete="new-password" />
        <label for="pwd-confirm">${escapeHtml(t('settings_pwd_confirm'))}</label>
        <input id="pwd-confirm" type="password" autocomplete="new-password" />
        <button type="button" id="btn-change-password">${escapeHtml(t('settings_btn_change_password'))}</button>
        <p id="pwd-change-status" class="status muted"></p>
      </section>

      <section class="card" id="totp-card">
        <h2 class="card-title">${escapeHtml(t('section_2fa'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_2fa_hint'))}</p>
        <p class="status ${totpOn ? 'ok' : 'muted'} small" id="totp-status-line">${escapeHtml(totpOn ? t('settings_2fa_on') : t('settings_2fa_off'))}</p>
        <div id="totp-setup" class="totp-setup" ${totpOn ? 'hidden' : ''}>
          <button type="button" id="btn-totp-start">${escapeHtml(t('settings_btn_enable_2fa'))}</button>
          <div id="totp-enroll" class="totp-enroll" hidden>
            <p class="status muted small">${escapeHtml(t('settings_2fa_scan'))}</p>
            <img id="totp-qr" class="totp-qr" alt="QR 2FA" width="200" height="200" />
            <p class="status muted small totp-secret-label">${escapeHtml(t('settings_2fa_secret'))}</p>
            <code id="totp-secret" class="totp-secret"></code>
            <label for="totp-confirm-code">${escapeHtml(t('settings_2fa_confirm_code'))}</label>
            <input id="totp-confirm-code" type="text" inputmode="numeric" maxlength="6" placeholder="000000" autocomplete="one-time-code" />
            <button type="button" id="btn-totp-confirm">${escapeHtml(t('settings_btn_confirm_2fa'))}</button>
            <button type="button" class="secondary" id="btn-totp-cancel">${escapeHtml(t('btn_back'))}</button>
          </div>
        </div>
        <button type="button" class="secondary danger-outline" id="btn-totp-disable" ${totpOn ? '' : 'hidden'}>${escapeHtml(t('settings_btn_disable_2fa'))}</button>
        <p id="totp-action-status" class="status muted"></p>
      </section>

      <section class="card" id="sound-card">
        <h2 class="card-title">${escapeHtml(t('section_sound'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_sound_hint'))}</p>
        ${toggleRow('toggle-sound-mute', t('toggle_sound_mute'), soundMuted, t('hint_sound_mute'))}
        <label for="sound-volume">${escapeHtml(t('settings_sound_volume'))} <span id="sound-volume-val">${soundVol}%</span></label>
        <input id="sound-volume" type="range" min="0" max="100" step="1" value="${soundVol}" ${soundMuted ? 'disabled' : ''} />
        <button type="button" class="secondary" id="btn-sound-test">${escapeHtml(t('settings_btn_sound_test'))}</button>
        <p id="sound-test-hint" class="status muted small"></p>
      </section>

      ${
        showWebPushSettings
          ? `
      <section class="card" id="push-settings-card">
        <h2 class="card-title">${escapeHtml(t('section_notifications'))}</h2>
        <p class="status muted small" id="push-status-line"></p>
        <p class="status err small" id="push-denied-hint" hidden></p>
        ${
          iosNeedsHomeInstall()
            ? `<p class="status muted small">${t('settings_push_hint_ios')}</p>`
            : ''
        }
        ${
          isStandalonePwa() && isAppleDevice()
            ? `<p class="status muted small">${t('settings_ios_push_hint')}</p>`
            : ''
        }
        <button type="button" id="btn-enable-push">${escapeHtml(t('settings_btn_enable_push'))}</button>
        <button type="button" class="secondary" id="btn-reset-push">${escapeHtml(t('settings_btn_reset_push'))}</button>
        <button type="button" class="secondary" id="btn-desktop-notify">${escapeHtml(t('settings_btn_desktop_notify'))}</button>
        <button type="button" class="secondary" id="btn-disable-push">${escapeHtml(t('settings_btn_disable_push'))}</button>
        <p id="push-action-status" class="status muted"></p>
      </section>
      `
          : ''
      }

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_exit'))}</h2>
        ${toggleRow('toggle-wipe-exit', t('toggle_wipe_exit'), getWipeOnExit(), t('hint_wipe_exit'))}
        <button type="button" class="secondary" id="btn-exit">${escapeHtml(t('btn_exit'))}</button>
      </section>

      <section class="card">
        <h2 class="card-title">${escapeHtml(t('section_backup'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_backup_hint'))}</p>
        <label for="backup-pass">${escapeHtml(t('settings_backup_pass_label'))}</label>
        <input id="backup-pass" type="password" autocomplete="new-password" placeholder="${escapeHtml(t('settings_backup_pass_ph'))}" />
        <button type="button" id="btn-export-backup">${escapeHtml(t('settings_btn_export_backup'))}</button>
        <label for="backup-file">${escapeHtml(t('settings_backup_file_label'))}</label>
        <input id="backup-file" type="file" accept="${BACKUP_FILE_ACCEPT}" />
        <p id="backup-file-name" class="status muted small"></p>
        <label for="backup-import-pass">${escapeHtml(t('settings_backup_import_pass_label'))}</label>
        <input id="backup-import-pass" type="password" autocomplete="current-password" placeholder="${escapeHtml(t('settings_backup_import_pass_ph'))}" />
        <button type="button" class="secondary" id="btn-import-backup">${escapeHtml(t('settings_btn_import_backup'))}</button>
        <p id="backup-status" class="status muted"></p>
      </section>

      <section class="card highlight support-card">
        <h2 class="card-title">${escapeHtml(t('section_support'))}</h2>
        <p class="status muted small">${escapeHtml(t('settings_support_hint'))}</p>
        <a class="btn-support-coffee" href="${escapeHtml(BUY_ME_A_COFFEE_URL)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('settings_btn_support_coffee'))}</a>
        <p class="status muted small support-note">${escapeHtml(t('settings_support_note'))}</p>
      </section>

      <section class="card">
        <button type="button" class="danger-btn fire-btn" id="btn-delete-all">${escapeHtml(t('btn_delete_all'))}</button>
      </section>
    `,
  )

  root.querySelector('#settings-lang')!.addEventListener('change', (e) => {
    const code = (e.target as HTMLSelectElement).value as LocaleCode
    saveLocale(code)
    setActiveLocale(code)
    void renderSettingsScreen(root)
  })

  root.querySelector('#toggle-external-links')!.addEventListener('change', (e) => {
    setAllowExternalLinks((e.target as HTMLInputElement).checked)
  })
  root.querySelector('#toggle-auto-media')!.addEventListener('change', (e) => {
    const on = (e.target as HTMLInputElement).checked
    setAutoMediaDownload(on)
    void getSessionToken().then((tok) => {
      if (tok) void syncMediaPrefs(tok, on).catch(() => {
        /* ignore media prefs sync failures */
      })
    })
  })
  root.querySelector('#toggle-wipe-exit')!.addEventListener('change', (e) => {
    setWipeOnExit((e.target as HTMLInputElement).checked)
  })

  const requireUnlockEl = root.querySelector('#toggle-require-unlock') as HTMLInputElement
  requireUnlockEl.addEventListener('change', () => {
    const wantOn = requireUnlockEl.checked
    if (wantOn) {
      setRequireUnlockOnOpen(true)
      return
    }
    requireUnlockEl.checked = true
    showWarnModal(t('warn_disable_password_title'), t('warn_disable_password_body'), () => {
      setRequireUnlockOnOpen(false)
      requireUnlockEl.checked = false
      markSessionUnlocked()
    })
  })

  root.querySelector('#btn-change-password')!.addEventListener('click', () => {
    void (async () => {
      const status = root.querySelector('#pwd-change-status') as HTMLElement
      const current = (root.querySelector('#pwd-current') as HTMLInputElement).value
      const next = (root.querySelector('#pwd-new') as HTMLInputElement).value
      const confirm = (root.querySelector('#pwd-confirm') as HTMLInputElement).value
      setStatus(status, t('settings_pwd_working'))
      try {
        await changeMasterPasswordFromSettings(current, next, confirm)
        ;(root.querySelector('#pwd-current') as HTMLInputElement).value = ''
        ;(root.querySelector('#pwd-new') as HTMLInputElement).value = ''
        ;(root.querySelector('#pwd-confirm') as HTMLInputElement).value = ''
        setStatus(status, t('settings_pwd_ok'), 'ok')
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        const map: Record<string, string> = {
          password_too_short: t('settings_pwd_err_short'),
          password_mismatch: t('settings_pwd_err_mismatch'),
          password_wrong: t('settings_pwd_err_wrong'),
          identity_incomplete: t('settings_pwd_err_identity'),
        }
        setStatus(status, map[msg] ?? t('settings_pwd_err_generic'), 'err')
      }
    })()
  })

  const totpEnroll = root.querySelector('#totp-enroll') as HTMLElement
  const totpActionStatus = root.querySelector('#totp-action-status') as HTMLElement
  root.querySelector('#btn-totp-start')?.addEventListener('click', () => {
    void (async () => {
      try {
        const { secret, otpauthUrl } = beginTotpEnrollment()
        const qr = root.querySelector('#totp-qr') as HTMLImageElement
        const secretEl = root.querySelector('#totp-secret') as HTMLElement
        secretEl.textContent = secret.match(/.{1,4}/g)?.join(' ') ?? secret
        qr.src = await renderQrDataUrl(otpauthUrl, 200)
        totpEnroll.hidden = false
        setStatus(totpActionStatus, t('settings_2fa_scan'), 'muted')
      } catch (err) {
        console.error('[ZION] TOTP QR failed', err)
        setStatus(
          totpActionStatus,
          err instanceof Error ? err.message : t('settings_2fa_confirm_fail'),
          'err',
        )
      }
    })()
  })
  root.querySelector('#btn-totp-cancel')?.addEventListener('click', () => {
    cancelTotpEnrollment()
    totpEnroll.hidden = true
    setStatus(totpActionStatus, '', 'muted')
  })
  root.querySelector('#btn-totp-confirm')?.addEventListener('click', () => {
    void (async () => {
      const code = (root.querySelector('#totp-confirm-code') as HTMLInputElement).value
      const ok = await confirmTotpEnrollment(code)
      if (!ok) {
        setStatus(totpActionStatus, t('settings_2fa_confirm_fail'), 'err')
        return
      }
      setStatus(totpActionStatus, t('settings_2fa_confirm_ok'), 'ok')
      void renderSettingsScreen(root)
    })()
  })
  root.querySelector('#btn-totp-disable')?.addEventListener('click', () => {
    showWarnModal(t('settings_2fa_disable_title'), t('settings_2fa_disable_body'), () => {
      disableTotp()
      void renderSettingsScreen(root)
    })
  })

  const volumeEl = root.querySelector('#sound-volume') as HTMLInputElement
  const volumeVal = root.querySelector('#sound-volume-val') as HTMLElement
  const muteEl = root.querySelector('#toggle-sound-mute') as HTMLInputElement
  volumeEl.addEventListener('input', () => {
    const v = Number(volumeEl.value)
    setSoundVolume(v)
    volumeVal.textContent = `${v}%`
  })
  muteEl.addEventListener('change', () => {
    const muted = muteEl.checked
    setSoundMuted(muted)
    volumeEl.disabled = muted
    void unlockNotificationAudio()
  })
  root.querySelector('#btn-sound-test')!.addEventListener('click', () => {
    if (isSoundMuted()) {
      setStatus(root.querySelector('#sound-test-hint') as HTMLElement, t('hint_sound_mute'), 'err')
      return
    }
    void playAudibleFeedbackTone(880, 0.2, 0.16).then((ok) => {
      const el = root.querySelector('#sound-test-hint') as HTMLElement
      if (!el) return
      if (ok) {
        setStatus(el, t('settings_sound_test_ok'), 'ok')
      } else {
        setStatus(el, t('settings_sound_test_fail'), 'err')
      }
    })
  })

  if (showWebPushSettings) {
    refreshPushSettingsUi(root)
    const pushActionStatus = root.querySelector('#push-action-status') as HTMLElement

    root.querySelector('#btn-enable-push')?.addEventListener('click', () => {
      const permissionPromise = beginNotificationPermissionRequest()
      void (async () => {
        setStatus(pushActionStatus, t('settings_push_requesting'))
        const token = await getSessionToken()
        if (!token) {
          setStatus(pushActionStatus, t('settings_push_no_session'), 'err')
          return
        }
        const result = await enableWebPush(token, { permissionPromise })
        if (!result.ok) {
          setStatus(pushActionStatus, result.reason, 'err')
          refreshPushSettingsUi(root)
          return
        }
        setStatus(pushActionStatus, t('settings_push_active'), 'ok')
        refreshPushSettingsUi(root)
      })()
    })

    root.querySelector('#btn-reset-push')?.addEventListener('click', () => {
      const permissionPromise = beginNotificationPermissionRequest()
      void (async () => {
        setStatus(pushActionStatus, t('settings_push_resetting'))
        const token = await getSessionToken()
        if (!token) {
          setStatus(pushActionStatus, t('settings_push_no_session'), 'err')
          return
        }
        const result = await resetAndReenableWebPush(token, { permissionPromise })
        if (!result.ok) {
          setStatus(pushActionStatus, result.reason, 'err')
          refreshPushSettingsUi(root)
          return
        }
        setStatus(pushActionStatus, t('settings_push_reset_ok'), 'ok')
        refreshPushSettingsUi(root)
      })()
    })

    root.querySelector('#btn-desktop-notify')?.addEventListener('click', () => {
      void (async () => {
        setStatus(pushActionStatus, t('settings_desktop_requesting'))
        const ok = await ensureDesktopNotificationPermission()
        if (!ok) {
          setStatus(
            pushActionStatus,
            pushPermission() === 'denied'
              ? notificationPermissionDeniedHint()
              : t('settings_desktop_denied'),
            'err',
          )
          refreshPushSettingsUi(root)
          return
        }
        setStatus(pushActionStatus, t('settings_desktop_ok'), 'ok')
        refreshPushSettingsUi(root)
      })()
    })

    root.querySelector('#btn-disable-push')?.addEventListener('click', () => {
      void (async () => {
        setStatus(pushActionStatus, t('settings_push_disabling'))
        const result = await disableWebPush()
        if (!result.ok) {
          setStatus(pushActionStatus, result.reason, 'err')
          return
        }
        setStatus(pushActionStatus, t('settings_push_disabled'), 'ok')
        refreshPushSettingsUi(root)
      })()
    })
  }

  root.querySelector('#btn-exit')!.addEventListener('click', () => {
    void exitApp()
  })

  const fontGrid = root.querySelector('#font-grid') as HTMLElement
  const activeFontId = getUserChatFontId()
  for (const font of FONTS_CONFIG) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `font-option${font.id === activeFontId ? ' active' : ''}`
    btn.dataset.fontId = font.id
    btn.style.fontFamily = font.family
    btn.innerHTML = `
      <span class="font-option-name">${escapeHtml(t(font.labelKey))}</span>
      <span class="font-option-sample">${escapeHtml(font.sample)}</span>
    `
    btn.addEventListener('click', () => {
      setUserChatFont(font.id as ChatFontId)
      fontGrid.querySelectorAll('.font-option').forEach((el) => el.classList.remove('active'))
      btn.classList.add('active')
    })
    fontGrid.append(btn)
  }

  const backupStatus = root.querySelector('#backup-status') as HTMLElement
  const backupFileInput = root.querySelector('#backup-file') as HTMLInputElement
  const backupFileName = root.querySelector('#backup-file-name') as HTMLElement
  let selectedBackupFile: File | null = null

  backupFileInput.addEventListener('change', () => {
    selectedBackupFile = backupFileInput.files?.[0] ?? null
    backupFileName.textContent = selectedBackupFile
      ? tf('settings_backup_file_prefix', { name: selectedBackupFile.name })
      : ''
  })
  if (isCapacitorNative()) {
    const addHome = root.querySelector('#btn-add-home') as HTMLButtonElement | null
    if (addHome) addHome.hidden = true
    root.querySelectorAll('p.status.muted.small').forEach((el) => {
      if (/PWA|Home Screen|Schermata Home|icona Home/i.test(el.textContent || '')) {
        ;(el as HTMLElement).hidden = true
      }
    })
  }

  const themeSwitch = root.querySelector('#theme-switch') as HTMLElement | null
  themeSwitch?.querySelectorAll('.theme-switch-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const raw = (btn as HTMLElement).dataset.theme
      const id: ZionThemeId =
        raw === 'matrix' ? 'matrix' : raw === 'white_platinum' ? 'white_platinum' : 'default'
      applyTheme(id)
      themeSwitch.querySelectorAll('.theme-switch-btn').forEach((el) => {
        el.classList.toggle('is-active', (el as HTMLElement).dataset.theme === id)
      })
    })
  })
  root.querySelector('#btn-back')!.addEventListener('click', () => goBackFromSettings())

  const deviceStatus = root.querySelector('#device-sync-status') as HTMLElement
  const devicesList = root.querySelector('#linked-devices-list') as HTMLElement
  const syncPcBtn = root.querySelector('#btn-sync-pc-history') as HTMLButtonElement

  void listLinkedDevices().then((devices) => {
    if (!devices.length) {
      devicesList.innerHTML = `<li class="status muted small">${escapeHtml(t('settings_no_devices'))}</li>`
      return
    }
    devicesList.innerHTML = devices
      .map(
        (d) =>
          `<li><strong>${escapeHtml(d.label)}</strong> <span class="muted">(${escapeHtml(d.platform)}${d.isSelf ? `, ${escapeHtml(t('settings_device_self'))}` : ''})</span><br><code class="small">${escapeHtml(d.deviceId.slice(0, 12))}…</code></li>`,
      )
      .join('')

    const platform = detectPlatform()
    const pcTarget = devices.find(
      (d) => !d.isSelf && (d.platform === 'desktop' || d.label.toLowerCase().includes('pc')),
    )
    if (platform === 'ios' || platform === 'android') {
      syncPcBtn.hidden = !pcTarget
      if (pcTarget) syncPcBtn.dataset.targetId = pcTarget.deviceId
    }
  })

  root.querySelector('#btn-link-new-device')!.addEventListener('click', () => {
    navigate(shouldShowDeviceLinkQrOnAuth() ? 'linkDevice' : 'linkDeviceScan')
  })

  syncPcBtn.addEventListener('click', () => {
    const targetId = syncPcBtn.dataset.targetId
    if (!targetId) return
    syncPcBtn.disabled = true
    void pushHistoryToDevice(targetId, (msg) => {
      deviceStatus.textContent = msg
      deviceStatus.className = `status cyber-status ${msg.includes('COMPLETE') ? 'ok' : 'muted'}`
    })
      .catch((err) => {
        deviceStatus.textContent = String(err)
        deviceStatus.className = 'status err'
      })
      .finally(() => {
        syncPcBtn.disabled = false
      })
  })

  root.querySelector('#btn-add-home')?.addEventListener('click', () => {
    if (isCapacitorNative()) return
    navigate('addToHome')
  })
  root.querySelector('#btn-lock')!.addEventListener('click', () => {
    disconnectSocket()
    lockSession()
    navigate('auth')
  })

  root.querySelector('#btn-export-backup')!.addEventListener('click', () => {
    void (async () => {
      const pass = (root.querySelector('#backup-pass') as HTMLInputElement).value
      if (pass.length < 8) {
        setStatus(backupStatus, t('settings_backup_pass_min'), 'err')
        return
      }
      setStatus(backupStatus, t('settings_backup_creating'), 'muted')
      try {
        const { blob, fileName, result } = await exportEncryptedBackup(pass, getCurrentTheme())
        if (!result.ok) {
          setStatus(backupStatus, result.message, 'err')
          return
        }
        downloadBackupBlob(blob, fileName)
        setStatus(backupStatus, result.message, 'ok')
      } catch (e) {
        setStatus(backupStatus, String(e), 'err')
      }
    })()
  })

  root.querySelector('#btn-import-backup')!.addEventListener('click', () => {
    void (async () => {
      const pass = (root.querySelector('#backup-import-pass') as HTMLInputElement).value
      const file = selectedBackupFile ?? backupFileInput.files?.[0] ?? null
      if (!file) {
        setStatus(backupStatus, t('settings_backup_pick_file'), 'err')
        return
      }
      if (!pass.trim()) {
        setStatus(backupStatus, t('settings_backup_need_pass'), 'err')
        return
      }
      setStatus(backupStatus, t('settings_backup_restoring'), 'muted')
      try {
        let result = await importEncryptedBackup(file, pass)
        if (!result.ok && result.message.includes('identità attuale')) {
          if (confirm(`${result.message}\n\n${t('settings_backup_overwrite_confirm')}`)) {
            result = await importEncryptedBackup(file, pass, { allowUserMismatch: true })
          }
        }
        if (!result.ok) {
          setStatus(backupStatus, result.message, 'err')
          return
        }
        selectedBackupFile = null
        backupFileInput.value = ''
        backupFileName.textContent = ''
        applyTheme(getCurrentTheme())
        markSessionUnlocked()
        try {
          await bootstrapRealtime()
        } catch {
          /* retry later */
        }
        setStatus(backupStatus, result.message, 'ok')
        setTimeout(() => navigate('home'), 800)
      } catch (e) {
        setStatus(backupStatus, String(e), 'err')
      }
    })()
  })

  root.querySelector('#btn-delete-all')!.addEventListener('click', () => {
    if (!confirm(t('warn_delete_all_1'))) return
    if (!confirm(t('warn_delete_all_2'))) return
    void (async () => {
      disconnectSocket()
      await factoryResetAllData()
      navigate('auth')
    })()
  })

  settingsSwipeCleanup?.()
  settingsSwipeCleanup = wireSwipeBack(root, goBackFromSettings).dispose
}

function goBackFromSettings(): void {
  navigate('home')
}
